import { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { experiencesService } from '../services';
import './ExperienceModal.css';

const ExperienceModal = ({ isOpen, onClose, buttonRef }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const modalRef = useRef(null);

  useEffect(() => {
    experiencesService.getAllExperiences()
      .then(setExperiences)
      .catch(err => console.error('Error loading experiences:', err));
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Get button position using getBoundingClientRect
      if (buttonRef?.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const buttonPosition = {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        };
        setButtonRect(buttonPosition);

        // Debug log
        console.log('Button position:', buttonPosition);
      }

      // Small delay to ensure DOM is ready and CSS vars are set
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);
    } else {
      // Unlock body scroll
      document.body.style.overflow = '';

      // Delay cleanup to allow closing animation
      setTimeout(() => {
        setIsAnimating(false);
        setButtonRect(null);
      }, 600); // Match the transition duration
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, buttonRef]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div
      className={`experience-modal-backdrop ${isAnimating && isOpen ? 'open' : 'closing'}`}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className={`experience-modal ${isAnimating && isOpen ? 'open' : 'closing'}`}
        style={{
          '--button-left': buttonRect ? `${buttonRect.left}px` : '50%',
          '--button-top': buttonRect ? `${buttonRect.top}px` : '50%',
          '--button-width': buttonRect ? `${buttonRect.width}px` : '200px',
          '--button-height': buttonRect ? `${buttonRect.height}px` : '50px',
        }}
      >
        <button className="experience-modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="experience-modal-scroll">
          <h2 className="experience-modal-title">My Experience</h2>

          <div className="experience-list">
            {experiences.map((exp) => (
              <div key={exp.id} className="experience-item">
                <div className="experience-header">
                  <h3 className="experience-role">{exp.role}</h3>
                  <span className="experience-period">
                    {exp.period?.toLowerCase().includes('present') && (
                      <span className="experience-active-dot" />
                    )}
                    {exp.period}
                  </span>
                </div>
                <h4 className="experience-company">{exp.company}</h4>
                {exp.subtitle && <p className="experience-subtitle">{exp.subtitle}</p>}
                {(exp.description || '').split('\n\n').map((para, i) => (
                  <p key={i} className="experience-description">{para}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceModal;
